// Ring App Store: Locations admin client.
//
// Distinct from the upstream `LocationSummary` shape used by the
// assignment builder's <LocationPicker> — this client returns the
// `Location` admin record (ring_location_id, status, version, …).
//
// The BFF's GET /api/locations is shared with the camera domain and
// returns the camera-domain shape today
// ({items: [{location_id, display_name, camera_count, online_camera_count,
// updated_at, address?, timezone?}], next_cursor}). The admin fields
// (display_name_source, status, version, first_seen_at, last_seen_at)
// aren't exposed there yet, so we adapt with sensible defaults so the
// admin page can still render and rename. Renames re-fetch on the next
// list, since the BFF response doesn't carry a version we can echo.

import { api, ApiError } from "./client";
import type { Location, RenameLocationRequest } from "@/types/location";

const BASE = "/api/locations";

interface CameraDomainLocationWire {
  location_id: string;
  display_name: string;
  address?: string | null;
  timezone?: string | null;
  camera_count?: number;
  online_camera_count?: number;
  updated_at?: string | null;
}

interface CameraDomainLocationListWire {
  items: CameraDomainLocationWire[];
  next_cursor: string | null;
}

interface AdminLocationWire {
  ring_location_id?: string;
  display_name?: string;
  display_name_source?: "auto" | "admin";
  country?: string | null;
  state?: string | null;
  status?: "active" | "removed" | "pending";
  first_seen_at?: string;
  last_seen_at?: string;
  version?: number;
}

function adaptListResponse(raw: unknown): Location[] {
  // The camera-domain wire shape is `{items: [...], next_cursor}`. The
  // admin shape would be a bare array. Detect both.
  if (Array.isArray(raw)) {
    return (raw as AdminLocationWire[]).map(adaptAdminWire).filter(notNull);
  }
  if (raw && typeof raw === "object") {
    const obj = raw as Partial<CameraDomainLocationListWire>;
    if (Array.isArray(obj.items)) {
      return obj.items.map(adaptCameraWire);
    }
  }
  return [];
}

function adaptCameraWire(w: CameraDomainLocationWire): Location {
  // Sensible defaults for fields the camera-domain endpoint doesn't carry.
  // The page treats these as advisory: status drives a badge, the
  // first/last-seen columns just show "-", and version=0 means rename
  // skips optimistic-concurrency (BFF accepts a missing/zero version).
  return {
    ring_location_id: w.location_id,
    display_name: w.display_name,
    display_name_source: "auto",
    country: null,
    state: null,
    status: "active",
    first_seen_at: w.updated_at ?? "",
    last_seen_at: w.updated_at ?? "",
    version: 0,
  };
}

function adaptAdminWire(w: AdminLocationWire): Location | null {
  if (!w.ring_location_id || !w.display_name) return null;
  return {
    ring_location_id: w.ring_location_id,
    display_name: w.display_name,
    display_name_source: w.display_name_source ?? "auto",
    country: w.country ?? null,
    state: w.state ?? null,
    status: w.status ?? "active",
    first_seen_at: w.first_seen_at ?? "",
    last_seen_at: w.last_seen_at ?? "",
    version: w.version ?? 0,
  };
}

function notNull<T>(v: T | null): v is T {
  return v !== null;
}

export const locationsClient = {
  /** GET /api/locations — tenant-scoped list. */
  async list(signal?: AbortSignal): Promise<Location[]> {
    const raw = await api.get<unknown>(BASE, { signal });
    return adaptListResponse(raw);
  },

  /** PATCH /api/locations/{ring_location_id} — rename with optimistic concurrency. */
  async rename(
    ringLocationId: string,
    body: RenameLocationRequest,
    signal?: AbortSignal,
  ): Promise<Location> {
    const res = await api.raw<unknown>(
      `${BASE}/${encodeURIComponent(ringLocationId)}`,
      { method: "PATCH", body, signal },
    );
    if (res === null) {
      throw new ApiError(0, "aborted", "request aborted", undefined, null);
    }
    // BFF may return either shape on PATCH; reuse the same adapter.
    if (Array.isArray(res.data)) {
      // Defensive — should never be a list, but normalize.
      const arr = adaptListResponse(res.data);
      const hit = arr.find((x) => x.ring_location_id === ringLocationId);
      if (hit) return hit;
    } else if (res.data && typeof res.data === "object") {
      const obj = res.data as AdminLocationWire & CameraDomainLocationWire;
      if (obj.ring_location_id) {
        const adapted = adaptAdminWire(obj);
        if (adapted) return adapted;
      }
      if (obj.location_id) {
        return adaptCameraWire(obj);
      }
    }
    // Fallback: synthesize a response so the page doesn't crash; the next
    // list refresh will correct the row.
    return {
      ring_location_id: ringLocationId,
      display_name: body.display_name,
      display_name_source: "admin",
      country: null,
      state: null,
      status: "active",
      first_seen_at: "",
      last_seen_at: "",
      version: body.version + 1,
    };
  },
};
